/**
 * PayrollBatchPage — Agency/Admin Payroll Processing
 *
 * Used by: CFSS (agency model), ALF (facility payroll), any industry with payroll
 * Shows approved timesheets and allows batch payroll runs.
 *
 * Route: /{industry}/payroll
 * Scope: payroll:run
 */

import React, { useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconBuildingBank,
  IconCalculator,
  IconCash,
  IconCheck,
  IconClock,
  IconPlayerPlay,
  IconUsers,
  IconAlertCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_APPROVED_TIMESHEETS = gql`
  query GetApprovedTimesheets($status: PayrollTimesheetStatus, $limit: Int) {
    payrollTimesheets(status: $status, limit: $limit) {
      id
      employeeId
      payPeriodStart
      payPeriodEnd
      status
      totalRegularHours
      totalOvertimeHours
      approvedAt
      payrollRunId
    }
  }
`;

const GET_PAYROLL_RUNS = gql`
  query GetPayrollRuns($status: PayrollStatus, $limit: Int) {
    payrollRuns(status: $status, limit: $limit) {
      id
      status
      payPeriod { startDate endDate payDate }
      totalGross { cents currency }
      totalNet { cents currency }
      employeeCount
      createdAt
    }
  }
`;

const RUN_PAYROLL = gql`
  mutation RunPayroll($input: PayrollRunPayrollInput!) {
    runPayroll(input: $input) {
      payrollRun {
        id
        status
        totalGross { cents currency }
        totalNet { cents currency }
        employeeCount
      }
      requiresApproval
    }
  }
`;

interface Timesheet {
  id: string;
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  approvedAt: string | null;
  payrollRunId: string | null;
}

interface PayrollRun {
  id: string;
  status: string;
  payPeriod: { startDate: string; endDate: string; payDate: string };
  totalGross: { cents: number; currency: string };
  totalNet: { cents: number; currency: string };
  employeeCount: number;
  createdAt: string;
}

interface RunPayrollResult {
  runPayroll: {
    payrollRun: {
      id: string;
      status: string;
      totalGross: { cents: number; currency: string };
      totalNet: { cents: number; currency: string };
      employeeCount: number;
    };
    requiresApproval: boolean;
  };
}

interface PayrollBatchPageProps {
  title?: string;
  subtitle?: string;
  workerLabel?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatMoney(money: { cents: number; currency: string } | null | undefined): string {
  if (!money) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: money.currency || 'USD' }).format(money.cents / 100);
}

export function PayrollBatchPage({
  title = 'Payroll',
  subtitle = 'Process approved timesheets into payroll runs',
  workerLabel = 'Worker',
  scope = 'payroll:run',
}: PayrollBatchPageProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [dryRun, setDryRun] = useState(true);

  const canRunPayroll = useHasScope(scope);

  const { data: timesheetsData, loading: timesheetsLoading, refetch } = useQuery<{ payrollTimesheets: Timesheet[] }>(
    GET_APPROVED_TIMESHEETS,
    { variables: { status: 'approved', limit: 100 } }
  );

  const { data: runsData, loading: runsLoading } = useQuery<{ payrollRuns: PayrollRun[] }>(GET_PAYROLL_RUNS, {
    variables: { limit: 10 },
  });

  const [runPayroll, { loading: running }] = useMutation<RunPayrollResult>(RUN_PAYROLL);

  const approvedTimesheets = (timesheetsData?.payrollTimesheets ?? []).filter((ts: Timesheet) => !ts.payrollRunId);
  const recentRuns = runsData?.payrollRuns ?? [];
  const loading = timesheetsLoading || runsLoading;

  const selectedTimesheets = approvedTimesheets.filter(ts => selectedIds.has(ts.id));
  const totalRegularHours = selectedTimesheets.reduce((sum, ts) => sum + ts.totalRegularHours, 0);
  const totalOvertimeHours = selectedTimesheets.reduce((sum, ts) => sum + ts.totalOvertimeHours, 0);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === approvedTimesheets.length) return new Set();
      return new Set(approvedTimesheets.map(ts => ts.id));
    });
  }, [approvedTimesheets]);

  const handleRunPayroll = useCallback(async () => {
    if (selectedTimesheets.length === 0) return;
    const firstTs = selectedTimesheets[0];
    if (!firstTs) return;

    const endDate = new Date(firstTs.payPeriodEnd);
    const payDate = new Date(endDate);
    payDate.setDate(payDate.getDate() + ((5 - payDate.getDay() + 7) % 7) + 1);

    try {
      const result = await runPayroll({
        variables: {
          input: {
            periodStart: firstTs.payPeriodStart,
            periodEnd: firstTs.payPeriodEnd,
            payDate: payDate.toISOString().split('T')[0],
            idempotencyKey: `payroll-${Date.now()}`,
            dryRun,
            employees: selectedTimesheets.map(ts => ({
              employeeId: ts.employeeId,
              regularHours: ts.totalRegularHours,
              overtimeHours: ts.totalOvertimeHours,
            })),
          },
        },
      });

      const payrollRun = result.data?.runPayroll?.payrollRun;
      if (dryRun) {
        notifications.show({ title: 'Dry Run Complete', message: `Preview: ${selectedTimesheets.length} employees, ${formatMoney(payrollRun?.totalGross)} gross`, color: 'blue' });
      } else {
        notifications.show({ title: 'Payroll Submitted', message: `Payroll run created for ${selectedTimesheets.length} workers.`, color: 'green' });
        setSelectedIds(new Set());
        setRunModalOpen(false);
        refetch();
      }
    } catch (err) {
      notifications.show({ title: 'Payroll Failed', message: err instanceof Error ? err.message : 'Error', color: 'red' });
    }
  }, [selectedTimesheets, dryRun, runPayroll, refetch]);

  if (loading) return <Center h="60vh"><Loader size="lg" /></Center>;

  if (!canRunPayroll) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to run payroll.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="green"><IconBuildingBank size={20} /></ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>{title}</Title>
              <Text size="sm" c="dimmed">{subtitle}</Text>
            </Stack>
          </Group>
          {selectedIds.size > 0 && (
            <Button leftSection={<IconPlayerPlay size={16} />} color="green" onClick={() => setRunModalOpen(true)}>
              Run Payroll ({selectedIds.size} workers)
            </Button>
          )}
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 4 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Ready for Payroll</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{approvedTimesheets.length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Selected</Text>
              <ThemeIcon size="sm" variant="light" color="blue"><IconUsers size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{selectedIds.size}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Total Regular</Text>
              <ThemeIcon size="sm" variant="light" color="gray"><IconClock size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{totalRegularHours.toFixed(2)}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Total Overtime</Text>
              <ThemeIcon size="sm" variant="light" color="orange"><IconCash size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={totalOvertimeHours > 0 ? 'orange' : undefined}>{totalOvertimeHours.toFixed(2)}</Text>
          </Card>
        </SimpleGrid>

        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600}>Approved Timesheets</Text>
            {approvedTimesheets.length > 0 && (
              <Button variant="subtle" size="sm" onClick={handleSelectAll}>
                {selectedIds.size === approvedTimesheets.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </Group>

          {approvedTimesheets.length === 0 ? (
            <Alert color="blue" title="No Timesheets Ready" icon={<IconClock />}>No approved timesheets ready for payroll.</Alert>
          ) : (
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}></Table.Th>
                    <Table.Th>{workerLabel}</Table.Th>
                    <Table.Th>Pay Period</Table.Th>
                    <Table.Th ta="right">Regular</Table.Th>
                    <Table.Th ta="right">OT</Table.Th>
                    <Table.Th ta="right">Total</Table.Th>
                    <Table.Th>Approved</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {approvedTimesheets.map(ts => (
                    <Table.Tr key={ts.id}>
                      <Table.Td><Checkbox checked={selectedIds.has(ts.id)} onChange={() => handleToggleSelect(ts.id)} /></Table.Td>
                      <Table.Td fw={500}>{ts.employeeId}</Table.Td>
                      <Table.Td>{formatDate(ts.payPeriodStart)} - {formatDate(ts.payPeriodEnd)}</Table.Td>
                      <Table.Td ta="right">{ts.totalRegularHours.toFixed(2)}</Table.Td>
                      <Table.Td ta="right"><Text c={ts.totalOvertimeHours > 0 ? 'orange' : undefined} size="sm">{ts.totalOvertimeHours.toFixed(2)}</Text></Table.Td>
                      <Table.Td ta="right" fw={600}>{(ts.totalRegularHours + ts.totalOvertimeHours).toFixed(2)}</Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{formatDate(ts.approvedAt)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Text fw={600}>Recent Payroll Runs</Text>
          {recentRuns.length === 0 ? (
            <Text size="sm" c="dimmed">No payroll runs yet.</Text>
          ) : (
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Pay Period</Table.Th>
                    <Table.Th>Pay Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th ta="right">Workers</Table.Th>
                    <Table.Th ta="right">Gross</Table.Th>
                    <Table.Th ta="right">Net</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {recentRuns.map(run => (
                    <Table.Tr key={run.id}>
                      <Table.Td>{formatDate(run.payPeriod.startDate)} - {formatDate(run.payPeriod.endDate)}</Table.Td>
                      <Table.Td>{formatDate(run.payPeriod.payDate)}</Table.Td>
                      <Table.Td>
                        <Badge color={run.status === 'completed' ? 'green' : run.status === 'processing' ? 'blue' : run.status === 'failed' ? 'red' : 'yellow'} size="sm">
                          {run.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right">{run.employeeCount}</Table.Td>
                      <Table.Td ta="right">{formatMoney(run.totalGross)}</Table.Td>
                      <Table.Td ta="right" fw={600}>{formatMoney(run.totalNet)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Stack>
      </Stack>

      <Modal opened={runModalOpen} onClose={() => setRunModalOpen(false)} title="Run Payroll" centered size="md">
        <Stack gap="md">
          <Alert color="blue" variant="light" icon={<IconCalculator />}>
            <Text size="sm">
              This will process <strong>{selectedTimesheets.length}</strong> timesheets totaling <strong>{(totalRegularHours + totalOvertimeHours).toFixed(2)}</strong> hours.
            </Text>
          </Alert>
          <SimpleGrid cols={2}>
            <Stack gap="xs"><Text size="sm" c="dimmed">Regular Hours</Text><Text size="lg" fw={600}>{totalRegularHours.toFixed(2)}</Text></Stack>
            <Stack gap="xs"><Text size="sm" c="dimmed">Overtime Hours</Text><Text size="lg" fw={600} c="orange">{totalOvertimeHours.toFixed(2)}</Text></Stack>
          </SimpleGrid>
          <Checkbox label="Dry run (preview only)" checked={dryRun} onChange={e => setDryRun(e.currentTarget.checked)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRunModalOpen(false)} disabled={running}>Cancel</Button>
            <Button color={dryRun ? 'blue' : 'green'} leftSection={dryRun ? <IconCalculator size={16} /> : <IconPlayerPlay size={16} />} onClick={handleRunPayroll} loading={running}>
              {dryRun ? 'Preview Payroll' : 'Run Payroll'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default PayrollBatchPage;
